/**
 * Converts resource URLs to embeddable iframe URLs for YouTube and Scribd/ebook viewers.
 * Used by Home (story cards) and Event page to open content in the fullscreen embed modal.
 */

/**
 * Returns true if the URL is a YouTube URL (watch, embed, youtu.be, music, etc.).
 */
export function isYoutubeUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return host.includes("youtube.com") || host.includes("youtu.be");
  } catch {
    return false;
  }
}

/**
 * Converts a URL to an embeddable form when possible (YouTube, Scribd).
 * Otherwise returns the original URL (many PDF/viewer URLs work in iframes).
 */
export function toEmbeddableUrl(inputUrl: string): string {
  try {
    const parsed = new URL(inputUrl);
    const host = parsed.hostname.toLowerCase();

    // YouTube: watch, share, youtu.be -> embed
    if (host.includes("youtube.com")) {
      if (parsed.pathname.startsWith("/embed/")) {
        return inputUrl;
      }
      const videoId = parsed.searchParams.get("v");
      if (videoId) {
        const embed = new URL(`https://www.youtube.com/embed/${videoId}`);
        ["start", "end", "t"].forEach((key) => {
          const value = parsed.searchParams.get(key);
          if (value) embed.searchParams.set(key, value);
        });
        return embed.toString();
      }
    }

    if (host.includes("youtu.be")) {
      const videoId = parsed.pathname.replace(/^\/+/, "").trim();
      if (videoId) {
        const embed = new URL(`https://www.youtube.com/embed/${videoId}`);
        parsed.searchParams.forEach((value, key) => {
          if (["start", "end", "t"].includes(key)) {
            embed.searchParams.set(key, value);
          }
        });
        return embed.toString();
      }
    }

    // Scribd: /document/123/... or /doc/123 -> /embeds/123/content
    if (host.includes("scribd.com")) {
      if (parsed.pathname.startsWith("/embeds/")) {
        return inputUrl;
      }
      const docMatch = parsed.pathname.match(
        /\/(?:document|doc|documents)\/(\d+)/i,
      );
      if (docMatch) {
        const docId = docMatch[1];
        return `https://www.scribd.com/embeds/${docId}/content`;
      }
    }

    return inputUrl;
  } catch {
    return inputUrl;
  }
}
